import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, RefreshCw } from 'lucide-react';
import { useGet } from '../../hooks/useApi.js';
import { PriorityBadge, SlaChip } from '../../components/ui/StatusBadge.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { relativeTime } from '../../lib/format.js';
import { cn } from '@hts/ui';

// API contract: GET /api/v1/ai/ticket-insights (apps/api/src/routes/ai-chat.ts).
// Counts are tenant-wide over OPEN tickets (not a single page of the list);
// items are the latest AI triage result per open ticket, newest first.
interface InsightItem {
  id: string;
  cwId: number;
  summary: string;
  board: string;
  status: string;
  priority: number;
  company: string;
  unassigned: boolean;
  slaDeadline: string;
  slaRisk: 'critical' | 'warning' | 'ok';
  confidence?: number;
  aiReason: string;
  estimatedHours?: number;
  triageKind: string;
  triagedAt: string;
}
interface InsightsResponse {
  summary: { openTickets: number; aiTriaged: number; highConfidence: number; slaAtRisk: number; unassigned: number };
  items: InsightItem[];
}

const KIND_LABELS: Record<string, string> = {
  dispatch_triage: 'Dispatch triage',
  auto_triage: 'Auto-triage',
  categorization: 'Categorization',
  title_rewrite: 'Title rewrite',
  duplicate_detection: 'Duplicate check',
  sentiment: 'Sentiment',
  customer_note: 'Customer note',
};

export function TicketInsightsPage() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useGet<InsightsResponse>('/api/v1/ai/ticket-insights?limit=100');
  const items = data?.items ?? [];
  const s = data?.summary;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Ticket Insights</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">AI-powered triage analysis, confidence scores, and routing reasons for open tickets.</p>
        </div>
        <button
          onClick={refetch}
          aria-label="Refresh insights"
          title="Refresh"
          className="p-2 rounded-lg text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))]"
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: s ? `AI-Triaged (of ${s.openTickets} open)` : 'AI-Triaged', value: s?.aiTriaged, icon: '🧠', color: 'text-blue-600' },
          { label: 'High Confidence (≥90%)', value: s?.highConfidence, icon: '✅', color: 'text-green-600' },
          { label: 'SLA at Risk (<4h)', value: s?.slaAtRisk, icon: '⚠️', color: 'text-amber-600' },
          { label: 'Unassigned', value: s?.unassigned, icon: '👤', color: 'text-red-600' },
        ].map(c => (
          <div key={c.label} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{c.icon}</span>
              <p className={`text-2xl font-bold tabular-nums ${c.color}`}>{loading && !s ? '…' : c.value ?? '—'}</p>
            </div>
            <p className="text-xs text-[hsl(var(--content-subtle))]">{c.label}</p>
          </div>
        ))}
      </div>

      {/* AI triage reasons table */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center gap-3">
          <Brain className="h-4 w-4 text-blue-600" />
          <h2 className="text-sm font-semibold text-[hsl(var(--content))]">Triage Reasoning</h2>
          <span className="ml-auto text-xs text-[hsl(var(--content-subtle))]">
            {s ? `showing ${items.length} of ${s.aiTriaged} AI-analyzed open tickets` : ''}
          </span>
        </div>
        <div className="divide-y divide-[hsl(var(--border))]">
          {loading && !data ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="px-5 py-4">
                <CardSkeleton lines={2} />
              </div>
            ))
          ) : error ? (
            <EmptyState
              icon="⚠️"
              title="Couldn't load ticket insights"
              description={error.includes('401') ? 'Your session expired — log out and back in.' : `${error} — try again.`}
              action={<button onClick={refetch} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Try again</button>}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon="🧠"
              title="No AI triage results yet"
              description={s && s.openTickets === 0
                ? 'No open tickets are synced yet. Check the ConnectWise integration and sync status.'
                : 'Open tickets have not been triaged yet. AI triage runs in the worker after each ConnectWise sync when the Claude integration is connected.'}
            />
          ) : (
            items.map(ticket => (
              <button
                key={ticket.id}
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                className="w-full text-left px-5 py-4 hover:bg-[hsl(var(--surface-subtle))] transition-colors"
              >
                <div className="flex items-start gap-4">
                  <PriorityBadge priority={ticket.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{ticket.summary}</p>
                      <span className="text-xs text-[hsl(var(--content-placeholder))] shrink-0">#{ticket.cwId}</span>
                    </div>
                    <p className="text-xs text-[hsl(var(--content-placeholder))] mt-0.5">
                      {ticket.company} · {ticket.board} · {KIND_LABELS[ticket.triageKind] ?? ticket.triageKind} {relativeTime(ticket.triagedAt)}
                    </p>
                    <div className="mt-2">
                      <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">Why AI routed this:</p>
                      <p className="text-sm text-[hsl(var(--content-subtle))]">{ticket.aiReason}</p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right space-y-1">
                    {ticket.confidence !== undefined && (
                      <div className="flex items-center gap-1.5 justify-end">
                        <div className="h-1.5 w-16 rounded-full bg-[hsl(var(--surface-subtle))]">
                          <div
                            className={cn('h-full rounded-full', ticket.confidence >= 0.9 ? 'bg-green-500' : ticket.confidence >= 0.75 ? 'bg-amber-500' : 'bg-red-500')}
                            style={{ width: `${ticket.confidence * 100}%` }}
                          />
                        </div>
                        <span className={cn('text-xs font-bold tabular-nums', ticket.confidence >= 0.9 ? 'text-green-600' : ticket.confidence >= 0.75 ? 'text-amber-600' : 'text-red-600')}>
                          {(ticket.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                    <SlaChip risk={ticket.slaRisk} deadline={ticket.slaDeadline} />
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
